/**
 * <card-product-siblings> — product-siblings swatche na card-pro.
 */
if (!customElements.get("card-product-siblings")) {
  // mm3: tasarımda kartta en fazla 5 renk noktası + "+N" (Wonder: 4 + "+ N").
  const MM3_MAX_DOTS = 5;
  const fragmentCache = new Map();
  // Rodzina kolorow ma wspolny prefiks SKU → jeden request na rodzine,
  // nie na karte (w siatce sasiaduje zwykle kilka kart tej samej rodziny).
  const lookupCache = new Map();

  customElements.define(
    "card-product-siblings",
    class CardProductSiblings extends HTMLElement {
      connectedCallback() {
        if (this.hasAttribute("data-card-siblings")) return this.initSiblings();
        if (this.hasAttribute("data-card-colors")) return this.initColors();
        if (this.hasAttribute("data-card-sibling-lookup")) return this.initLookup();
      }

      /**
       * Produkt rozbity na kolory BEZ metapola custom.product_siblings.
       * Rodzenstwo dociagamy po prefiksie SKU; jeden request na rodzine
       * (cache), odpalany dopiero gdy karta wchodzi w widok.
       */
      initLookup() {
        const query = this.dataset.lookupQ;
        if (!query) return;

        let started = false;
        const start = () => {
          if (started) return;
          started = true;
          this._lookupObserver?.disconnect();
          this.removeEventListener("pointerenter", start);
          this.loadLookup(query);
        };

        if ("IntersectionObserver" in window) {
          this._lookupObserver = new IntersectionObserver(
            (entries) => {
              if (entries.some((e) => e.isIntersecting)) start();
            },
            { rootMargin: "300px" },
          );
          this._lookupObserver.observe(this);
          // Zapasowy wyzwalacz: IntersectionObserver nie odpala sie w ukrytej
          // karcie przegladarki, a najechanie myszka to jednoznaczna intencja.
          this.addEventListener("pointerenter", start);
        } else {
          start();
        }
      }

      async loadLookup(query) {
        if (!lookupCache.has(query)) {
          lookupCache.set(
            query,
            fetch(
              `/search?q=${encodeURIComponent(query)}&view=sibling-swatches&type=product`,
            )
              .then((res) => (res.ok ? res.text() : ""))
              .catch(() => ""),
          );
        }

        const html = await lookupCache.get(query);
        if (!html) return;

        const doc = new DOMParser().parseFromString(html, "text/html");
        const container = doc.querySelector(".card__color-swatcher--container");
        if (!container) return;

        const found = container.querySelectorAll(".color-swatcher--wrapper");
        // Jeden wynik = produkt nie ma rodzenstwa; nie pokazuj samotnego swatcha.
        if (found.length < 2) return;

        const current = this.dataset.currentHandle;
        found.forEach((w) =>
          w.classList.toggle("active", w.dataset.siblingHandle === current),
        );

        const host = this.querySelector(".card__container");
        if (!host) return;
        host.appendChild(container);

        // Od tego momentu karta jest zwyklym rodzenstwem.
        this.removeAttribute("data-card-sibling-lookup");
        this.setAttribute("data-card-siblings", "");
        this.initSiblings();
      }

      /**
       * Swatche z prawdziwej opcji wariantu (Color) — produkt bez rodzenstwa.
       * Klik: zaznacza kolor, przewija galerie karty na zdjecie tego koloru
       * i przestawia linki karty na URL wariantu.
       */
      initColors() {
        this.container = this.querySelector(".card__color-swatcher--container");
        if (!this.container) return;

        this.gallery = this.querySelector("card-gallery");
        this.track = this.querySelector("[data-gallery-track]");
        this.indicator = this.querySelector("[data-gallery-indicator]");
        this.cardLinks = Array.from(this.querySelectorAll("a.card, .card__title a"));

        this.wrappers = Array.from(
          this.container.querySelectorAll(".color-swatcher--wrapper"),
        );
        this.initCounter();
        this.mm3TouchOnly();

        this.wrappers.forEach((wrapper, index) => {
          if (index >= MM3_MAX_DOTS) wrapper.classList.add("hidden");
          const tooltip = wrapper.querySelector(".color-swatcher--tooltip");
          wrapper.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.selectColor(wrapper);
          });
          wrapper.addEventListener("keydown", (e) => {
            if (e.key !== "Enter" && e.key !== " ") return;
            e.preventDefault();
            this.selectColor(wrapper);
          });
          if (tooltip) {
            wrapper.addEventListener("mouseover", () => this.showTooltip(tooltip));
            wrapper.addEventListener("mouseout", () => this.hideTooltip(tooltip));
          }
        });
      }

      selectColor(wrapper) {
        if (wrapper.classList.contains("active")) return;
        this.wrappers.forEach((w) => w.classList.remove("active"));
        wrapper.classList.add("active");

        const url = wrapper.dataset.variantUrl;
        if (url) this.cardLinks.forEach((a) => a.setAttribute("href", url));

        const galleryReady =
          this.gallery && this.gallery.hasAttribute("data-card-gallery");

        // 1) Zdjecia tej barwy → przebuduj tor galerii (bez zostawiania kadrow
        //    w poprzednim kolorze).
        const slides = this.parseSlides(wrapper);
        if (slides.length && this.track) {
          // mm3: yeniden kurulan karelere de anlamlı alt ("Ürün adı in Renk"); önceden alt hiç yoktu.
          const mm3Title = (this.querySelector(".card__title a")?.textContent || "").trim();
          const mm3Color = wrapper.dataset.colorValue || "";
          const mm3Alt = mm3Title && mm3Color ? `${mm3Title} in ${mm3Color}` : mm3Title;
          // mm3: yeni karelere bölümün sizes değeri (2/4 sütun seçicisinin güncellediği) — sabit 620px değil
          const mm3Sizes = this.track.querySelector("img.card__img")?.getAttribute("sizes") || "(min-width: 1000px) 620px, 100vw";
          this.track.innerHTML = "";
          slides.forEach((s, i) => {
            const div = document.createElement("div");
            div.className = "card-pro__gallery-slide";
            const img = document.createElement("img");
            img.className = "card__img";
            img.alt = mm3Alt;
            img.src = s.s;
            if (s.ss) img.srcset = s.ss;
            img.sizes = mm3Sizes;
            img.loading = i === 0 ? "eager" : "lazy";
            div.appendChild(img);
            this.track.appendChild(div);
          });
          this.rebuildIndicator();
          this.gallery?.refresh?.();
          return;
        }

        // 2) Brak listy zdjec → jesli barwa jest w oknie galerii, po prostu przewin.
        const slide = wrapper.dataset.mediaSlide;
        if (slide !== undefined && slide !== "") {
          const index = parseInt(slide, 10);
          if (!Number.isNaN(index) && galleryReady && this.gallery.goTo) {
            this.gallery.goTo(index);
          }
        }
      }

      parseSlides(wrapper) {
        if (wrapper._slides) return wrapper._slides;
        const raw = (wrapper.dataset.slides || "").trim();
        // Lista adresow 1000w rozdzielona spacja; srcset skladamy podmieniajac
        // parametr width — CDN Shopify obsluguje dowolna szerokosc.
        const out = raw ? raw.split(/\s+/).filter(Boolean).map((u) => ({
          s: u,
          ss: [375, 750, 1000, 1440]
            .map((w) => `${u.replace(/([?&]width=)\d+/, `$1${w}`)} ${w}w`)
            .join(", "),
        })) : [];
        wrapper._slides = out;
        return out;
      }

      initSiblings() {
        this.container = this.querySelector(".card__color-swatcher--container");
        if (!this.container) return;

        this.gallery = this.querySelector("card-gallery");
        this.track = this.querySelector("[data-gallery-track]");
        this.indicator = this.querySelector("[data-gallery-indicator]");
        this.cardLink = this.querySelector(".card");
        this.titleLink = this.querySelector(".card__title a");
        this.priceEl = this.querySelector(".card__price");
        this.ratingSlot = this.querySelector(".card__rating-slot");
        this.quickAdds = this.querySelectorAll("quick-add");
        this.qbp = this.querySelector("quick-size-picker");
        this.loader = this.querySelector(".card__loader");

        this.galleryLimit = parseInt(this.dataset.galleryLimit, 10) || 5;
        this.showRating = this.dataset.showRating === "true";
        this.hideWhenNoReviews = this.dataset.hideWhenNoReviews === 'true';


        this.sortButtons();
        this.wrappers = Array.from(
          this.container.querySelectorAll(".color-swatcher--wrapper"),
        );
        this.initCounter();

        this.wrappers.forEach((wrapper, index) => {
          if (index >= MM3_MAX_DOTS) wrapper.classList.add("hidden");
          const tooltip = wrapper.querySelector(".color-swatcher--tooltip");
          wrapper.addEventListener("click", () => this.select(wrapper));
          wrapper.addEventListener("keydown", (e) => {
            if (e.key === "Enter") this.select(wrapper);
          });
          wrapper.addEventListener("mouseenter", () =>
            this.prefetch(wrapper.dataset.siblingUrl),
          );
          wrapper.addEventListener("focusin", () =>
            this.prefetch(wrapper.dataset.siblingUrl),
          );
          if (tooltip) {
            wrapper.addEventListener("mouseover", () => this.showTooltip(tooltip));
            wrapper.addEventListener("mouseout", () => this.hideTooltip(tooltip));
          }
        });
      }

      fetchFragment(url) {
        if (!url) return Promise.reject(new Error("no url"));
        if (fragmentCache.has(url)) return fragmentCache.get(url);

        const u = new URL(url, window.location.origin);
        u.searchParams.set("view", "card-pro-sibling");

        const promise = fetch(u.toString())
          .then((res) => {
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return res.text();
          })
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, "text/html");
            const qbpEl = doc.querySelector("[data-sib-qbp]");
            return {
              trackHtml: doc.querySelector("[data-sib-track]")?.innerHTML || "",
              priceHtml: doc.querySelector("[data-sib-price]")?.innerHTML || "",
              ratingHtml: doc.querySelector("[data-sib-rating]")?.innerHTML || "",
              ratingValue: doc.querySelector("[data-sib-rating]")?.dataset.sibRatingValue || "",
              qbpRowsHtml:
                doc.querySelector("[data-sib-qbp-rows]")?.innerHTML || "",
              qbpSingleId: qbpEl?.dataset.singleId || "",
              qbpSingleAvailable: qbpEl?.dataset.singleAvailable === "true",
            };
          })
          .catch((err) => {
            fragmentCache.delete(url);
            throw err;
          });

        fragmentCache.set(url, promise);
        return promise;
      }

      prefetch(url) {
        this.fetchFragment(url).catch(() => {});
      }

      async select(wrapper) {
        if (wrapper.classList.contains("active")) return;
        const url = wrapper.dataset.siblingUrl;
        const handle = wrapper.dataset.siblingHandle;
        const title = wrapper.dataset.siblingTitle;

        const prevActive = this.container.querySelector(
          ".color-swatcher--wrapper.active",
        );
        this.wrappers.forEach((w) => w.classList.remove("active"));
        wrapper.classList.add("active");
        this.showLoader();
        // Guard na race: jeśli w międzyczasie kliknięto inny swatch, porzuć wynik.
        this._activeUrl = url;

        let data;
        try {
          data = await this.fetchFragment(url);
        } catch (err) {
          wrapper.classList.remove("active");
          prevActive?.classList.add("active");
          this.hideLoader();
          return;
        }

        if (url !== this._activeUrl) return;

        if (this.track) {
          const temp = document.createElement("div");
          temp.innerHTML = data.trackHtml;
          const slides = Array.from(
            temp.querySelectorAll(".card-pro__gallery-slide"),
          ).slice(0, this.galleryLimit);
          this.track.innerHTML = "";
          slides.forEach((s) => this.track.appendChild(s));
          this.rebuildIndicator();
          this.gallery?.refresh?.();
          this.bindLoader();
        } else {
          this.hideLoader();
        }

        if (this.titleLink) {
          this.titleLink.textContent = title;
          if (url) this.titleLink.setAttribute("href", url);
        }
        if (url) this.cardLink?.setAttribute("href", url);

        if (this.priceEl && data.priceHtml) this.priceEl.innerHTML = data.priceHtml;



        this.quickAdds?.forEach((qa) => {
          if (handle) qa.setAttribute("data-product-handle", handle);
          const btn = qa.querySelector("button");
          if (btn && url) btn.dataset.productUrl = url;
        });

        this.qbp?.refreshVariants?.({
          rowsHtml: data.qbpRowsHtml,
          singleId: data.qbpSingleId,
          singleAvailable: data.qbpSingleAvailable,
        });

        if (!this.ratingSlot || !this.showRating) return;
        this.ratingSlot.innerHTML =
          this.hideWhenNoReviews && data.ratingValue === '' ? '' : data.ratingHtml;
      }

      rebuildIndicator() {
        if (!this.indicator) return;
        const count = this.track.querySelectorAll(".card-pro__gallery-slide").length;
        this.indicator.innerHTML = "";
        for (let i = 0; i < count; i++) {
          const seg = document.createElement("span");
          seg.className =
            "card-pro__gallery-indicator__seg" + (i === 0 ? " is-active" : "");
          this.indicator.appendChild(seg);
        }
      }

      bindLoader() {
        const firstImg = this.track.querySelector("img");
        if (firstImg && !firstImg.complete) {
          firstImg.addEventListener("load", () => this.hideLoader(), { once: true });
          firstImg.addEventListener("error", () => this.hideLoader(), { once: true });
        } else {
          this.hideLoader();
        }
      }

      showLoader() {
        if (!this.loader) return;
        this.loader.classList.remove("hidden");
        this.loader.innerHTML = '<div class="spinner-ring"></div>';
      }

      hideLoader() {
        if (!this.loader) return;
        this.loader.classList.add("hidden");
        this.loader.innerHTML = "";
      }

      showTooltip(tooltip) {
        tooltip.classList.remove("hidden");
        const coords = tooltip.getBoundingClientRect();
        if (coords.x < 0) tooltip.classList.add("color-swatcher--tooltip-left");
        else if (coords.right > window.innerWidth)
          tooltip.classList.add("color-swatcher--tooltip-right");
      }

      hideTooltip(tooltip) {
        tooltip.classList.add("hidden");
        tooltip.classList.remove(
          "color-swatcher--tooltip-left",
          "color-swatcher--tooltip-right",
        );
      }

      initCounter() {
        if (this.wrappers.length <= MM3_MAX_DOTS) return;
        this.counter = this.container.querySelector(".color-swatcher--counter");
        if (!this.counter) {
          this.counter = document.createElement("span");
          this.counter.classList.add("color-swatcher--counter");
          this.container.appendChild(this.counter);
        }
        this.counter.innerHTML += `+${this.wrappers.length - MM3_MAX_DOTS}`;
        this.counter.addEventListener("click", () => this.showAll());
      }

      // mm3: dokunmatikte noktalar yalnız gösterge (decisions collection-04, mm3-card.css pointer-events:none).
      // Kart başına 5 odak durağı olmasın: noktalar sekme sırasından çıkar, ekran okuyucu tek özet okur ("Colours: …").
      mm3TouchOnly() {
        if (!window.matchMedia || !window.matchMedia("(hover: none), (pointer: coarse)").matches) return;
        const names = this.wrappers.map((w) => w.dataset.colorValue).filter(Boolean);
        this.container.setAttribute("role", "img");
        this.container.setAttribute("aria-label", `Colours: ${names.join(", ")}`);
        this.container.querySelectorAll('[tabindex="0"]').forEach((el) => {
          el.setAttribute("tabindex", "-1");
          el.setAttribute("aria-hidden", "true");
        });
      }

      showAll() {
        this.wrappers.forEach((w) => w.classList.remove("hidden"));
        this.counter?.classList.add("hidden");
      }

      sortButtons() {
        const arr = Array.from(
          this.container.querySelectorAll(".color-swatcher--wrapper"),
        );
        this.container.innerHTML = "";
        arr.forEach((el) => this.container.append(el));
        const counter = document.createElement("span");
        counter.classList.add("color-swatcher--counter");
        this.container.appendChild(counter);
      }
    },
  );
}
